// packages/core/src/economy/EconomyTypes.ts
// ─────────────────────────────────────────────────────────────────────────
// Gem economy types. Server is the source of truth for balances and
// transactions. Client uses these types as the read-only cache shape.
// ─────────────────────────────────────────────────────────────────────────

/** Locked enum of every event that can earn gems.
 *  ADDITIVE-ONLY: never rename or remove a member; new earners are appended
 *  (same pattern as impact_node_reward). */
export type EarnEventType =
  | 'daily_login'             // logged in today
  | 'streak_milestone'        // hit a streak threshold (3, 7, 14, 30, 60, 100, ...)
  | 'level_up'                // character leveled up
  | 'quiz_pass_first_try'     // passed a module quiz on first attempt
  | 'quiz_pass_subsequent'    // passed on a later attempt
  | 'quiz_aced'               // scored 100% on a quiz (bonus on top of the pass)
  | 'module_complete'         // mastered a module
  | 'impact_node_reward'      // completed a Core Path bonus decision node (Impact Lab V2)
  | 'lesson_audio_complete'   // listened to a full lesson audio
  | 'playlist_complete'       // listened to a full custom playlist
  | 'final_test_pass'         // passed the tier/final test
  | 'discovery_quiz_complete' // completed a Discovery quiz (once per quiz)
  | 'daily_review_cleared'    // cleared every due card in Daily Review (once per UTC day)
  | 'lesson_applied'          // completed a lesson Apply action (DORMANT until apply ships)
  | 'scenario_first_saved'    // saved a first scenario in Simulate (once ever)
  | 'market_replay_first'     // finished a first Market Replay run (once ever)
  | 'school_exhibit_complete' // read a school exhibit to the end (once per school)
  | 'first_install'           // welcome bonus
  | 'iap_purchase';           // bought gems

/** Locked enum of how gems can be spent. */
export type SpendEventType =
  | 'character_skin'
  | 'streak_freeze'           // protects a streak for one missed day
  | 'theme_unlock'
  | 'cosmetic_effect'
  | 'admin_adjustment';       // for support/customer-service corrections

export interface GemBalance {
  userId: string;
  total: number;              // current gems available to spend
  lifetimeEarned: number;     // sum of all earn events ever
  lifetimeSpent: number;      // sum of all spend events ever
  lifetimePurchased: number;  // sum of IAP-credited gems
  updatedAt: number;          // ms since epoch
}

export interface GemTransaction {
  id: string;                 // UUID
  userId: string;
  direction: 'earn' | 'spend';
  amount: number;             // always positive
  eventType: EarnEventType | SpendEventType;
  entityId?: string;          // lessonId / moduleId / itemId — context
  metadata?: Record<string, unknown>;
  createdAt: number;
  /**
   * Server-validated. Local-only transactions stay 'pending' until sync,
   * then become 'confirmed' or 'rejected' based on server verdict.
   */
  status: 'pending' | 'confirmed' | 'rejected';
  rejectReason?: string;
}

// NOTE: the DailyEarningCaps interface (organicMax) was removed with the
// daily gem cap. Organic earning is uncapped by design — the idempotency
// ledger plus the sync rate limits in EarningRules are the abuse rails.

/** Empty/initial balance for first install. */
export function emptyGemBalance(userId: string): GemBalance {
  return {
    userId,
    total: 0,
    lifetimeEarned: 0,
    lifetimeSpent: 0,
    lifetimePurchased: 0,
    updatedAt: Date.now(),
  };
}
